import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/api';
import { Wrench, FileText, Truck, AlertTriangle, Loader2, Plus, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { MultiRepairMap } from '@/components/MapView';
import { STATUS_COLORS } from '@/constants/statusColors';

const PAYMENT_COLORS = { paid:'text-[var(--success)]',unpaid:'text-[var(--warning)]',overdue:'text-[var(--error)]' };

export default function PortalDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/dashboard/customer');
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin" size={28} style={{ color: 'var(--primary)' }} /></div>;

  const { active_repairs = [], spend = {}, recent_invoices = [], unit_count = 0, unpaid_count = 0, benchmarking = {} } = data || {};
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="portal-dashboard-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-['Manrope'] font-bold text-3xl" style={{ color: 'var(--text-primary)' }}>Fleet Overview</h1>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Welcome back, {user?.full_name}</p>
        </div>
      </div>

      {/* Cost Per Mile Benchmarking Widget */}
      {benchmarking.cost_per_mile > 0 && (
        <div className="border rounded-lg p-5" style={{ background: 'linear-gradient(to right, var(--surface), var(--bg-darker))', borderColor: 'var(--border)' }} data-testid="benchmarking-widget">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>YOUR COST PER MILE</p>
              <div className="flex items-baseline gap-3">
                <span className={`text-3xl font-['JetBrains_Mono'] font-bold ${benchmarking.cost_per_mile <= benchmarking.industry_average ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>
                  ${benchmarking.cost_per_mile?.toFixed(3)}
                </span>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>vs industry avg ${benchmarking.industry_average?.toFixed(3)}</span>
              </div>
            </div>
            <div className="text-right">
              {benchmarking.percent_better > 0 ? (
                <div className="flex items-center gap-2" style={{ color: 'var(--success)' }}>
                  <TrendingDown size={20} />
                  <span className="text-xl font-['JetBrains_Mono'] font-bold">{benchmarking.percent_better?.toFixed(0)}%</span>
                  <span className="text-sm">below avg</span>
                </div>
              ) : benchmarking.percent_better < 0 ? (
                <div className="flex items-center gap-2 text-orange-400">
                  <TrendingUp size={20} />
                  <span className="text-xl font-['JetBrains_Mono'] font-bold">{Math.abs(benchmarking.percent_better)?.toFixed(0)}%</span>
                  <span className="text-sm">above avg</span>
                </div>
              ) : null}
              {benchmarking.savings_vs_avg > 0 && (
                <p className="text-xs mt-1" style={{ color: 'var(--success)' }}>
                  Saving ~${benchmarking.savings_vs_avg?.toLocaleString()} vs industry average
                </p>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4 relative">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-darker)' }}>
              <div 
                className={`h-full rounded-full transition-all ${benchmarking.cost_per_mile <= benchmarking.industry_average ? 'bg-[var(--success)]' : 'bg-[var(--warning)]'}`}
                style={{ width: `${Math.min((benchmarking.cost_per_mile / 0.35) * 100, 100)}%` }}
              />
            </div>
            {/* Industry average marker */}
            <div 
              className="absolute top-0 w-0.5 h-4 -translate-y-1"
              style={{ left: `${(benchmarking.industry_average / 0.35) * 100}%`, background: 'rgba(255,255,255,0.5)' }}
            />
            <div className="flex justify-between mt-1 text-[9px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
              <span>$0.00</span>
              <span>$0.35</span>
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Repairs', value: active_repairs.length, icon: Wrench, color: 'var(--primary)', bg: 'var(--primary-muted)', borderColor: 'var(--primary)' },
          { label: 'This Month', value: fmt(spend.this_month), icon: FileText, color: 'rgb(96, 165, 250)', bg: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.3)', mono: true },
          { label: 'YTD Spend', value: fmt(spend.ytd), icon: FileText, color: 'rgb(45, 212, 191)', bg: 'rgba(20, 184, 166, 0.1)', borderColor: 'rgba(20, 184, 166, 0.3)', mono: true },
          { label: 'My Units', value: unit_count, icon: Truck, color: 'var(--text-muted)', bg: 'var(--surface)', borderColor: 'var(--border)' },
        ].map(({ label, value, icon: Icon, color, bg, borderColor, mono }) => (
          <div key={label} className="rounded-md border p-5" style={{ background: bg, borderColor }} data-testid={`portal-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{label}</span>
              <Icon size={16} style={{ color }} />
            </div>
            <p className={`text-2xl font-bold ${mono ? "font-['JetBrains_Mono']" : "font-['Manrope']"}`} style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Map of Active Repairs */}
      {active_repairs.length > 0 && (
        <div>
          <h2 className="font-['Manrope'] font-bold text-lg mb-4" style={{ color: 'var(--text-primary)' }}>Fleet Map</h2>
          <div className="rounded-md overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
            <MultiRepairMap repairs={active_repairs} height="320px" />
          </div>
          {active_repairs.filter(r => !r.lat).length > 0 && (
            <p className="font-['IBM_Plex_Sans'] text-xs mt-2" style={{ color: 'var(--text-faint)' }}>
              {active_repairs.filter(r => !r.lat).length} repair(s) not yet pinned on map — your ops team can add locations from the repair detail page.
            </p>
          )}
        </div>
      )}

      {/* Active Repairs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Manrope'] font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Active Repairs</h2>
          <button onClick={() => navigate('/portal/repairs')} className="text-sm hover:underline font-['IBM_Plex_Sans']" style={{ color: 'var(--primary)' }}>View all</button>
        </div>
        {active_repairs.length === 0 ? (
          <div className="py-12 text-center rounded-md border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <Wrench size={28} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
            <p className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No active repairs — your fleet is running smooth</p>
          </div>
        ) : (
          <div className="space-y-3">
            {active_repairs.map((r) => (
              <div key={r.id} className="flex items-center gap-4 p-4 rounded-md border transition-colors"
                style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                data-testid={`portal-repair-${r.id}`}>
                {r.priority === 'emergency' && <AlertTriangle size={18} className="flex-shrink-0" style={{ color: 'var(--error)' }} />}
                <span className="font-['JetBrains_Mono'] text-sm font-bold flex-shrink-0" style={{ color: 'var(--primary)' }}>{r.truck_info?.split('—')[0]?.trim() || 'N/A'}</span>
                <span className="text-sm font-['IBM_Plex_Sans'] flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{r.issue_description}</span>
                <span className={`flex-shrink-0 inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${STATUS_COLORS[r.status] || STATUS_COLORS.open}`}>
                  {r.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Invoices */}
      {recent_invoices.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Manrope'] font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Recent Invoices</h2>
            <button onClick={() => navigate('/portal/invoices')} className="text-sm hover:underline font-['IBM_Plex_Sans']" style={{ color: 'var(--primary)' }}>View all</button>
          </div>
          {unpaid_count > 0 && (
            <div className="mb-3 px-4 py-3 rounded-md flex items-center gap-3" style={{ background: 'var(--warning-muted)', border: '1px solid var(--warning)' }}>
              <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
              <span className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--warning)' }}>{unpaid_count} invoice{unpaid_count > 1 ? 's' : ''} outstanding</span>
            </div>
          )}
          <div className="space-y-2">
            {recent_invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 p-4 rounded-md border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid={`portal-invoice-${inv.id}`}>
                <span className="font-['JetBrains_Mono'] text-xs" style={{ color: 'var(--text-muted)' }}>{inv.invoice_number}</span>
                <span className="text-sm font-['IBM_Plex_Sans'] flex-1" style={{ color: 'var(--text-primary)' }}>{inv.vendor_name || inv.category || '—'}</span>
                <span className="font-['JetBrains_Mono'] text-sm font-bold" style={{ color: 'var(--text-primary)' }}>${inv.amount?.toLocaleString()}</span>
                <span className={`text-xs font-bold uppercase font-['IBM_Plex_Sans'] ${PAYMENT_COLORS[inv.payment_status] || ''}`} style={{ color: PAYMENT_COLORS[inv.payment_status] ? undefined : 'var(--text-muted)' }}>{inv.payment_status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
